import { useEffect } from 'react';
import { bindBackButton, bindMainButton } from '../telegram/sdk';
import { useSurface } from '../hooks/useSurface';

/**
 * MainButton and BackButton are Telegram-native chrome. Both components render
 * nothing into the DOM: they only drive the host UI, and they are inert on the
 * web surfaces, where the FAB (mobile) and the header button (desktop) take
 * over. That is what keeps the "FAB or MainButton, never both" rule structural
 * rather than a per-screen condition.
 */

export function MainButton({
  label,
  onClick,
  loading,
}: {
  label: string | null;
  onClick: () => void;
  loading?: boolean;
}) {
  const { isTelegram } = useSurface();

  useEffect(() => {
    if (!isTelegram) return;
    let dispose: (() => void) | undefined;
    let cancelled = false;

    // Hidden while loading — the spec's `showMainButton = !!label && !isLoading`.
    const config = label && !loading ? { text: label, onClick } : null;
    void bindMainButton(config).then((fn) => {
      if (cancelled) fn();
      else dispose = fn;
    });

    return () => {
      cancelled = true;
      dispose?.();
    };
  }, [isTelegram, label, onClick, loading]);

  return null;
}

export function BackButton({ onBack }: { onBack: (() => void) | null }) {
  const { isTelegram } = useSurface();

  useEffect(() => {
    if (!isTelegram) return;
    let dispose: (() => void) | undefined;
    let cancelled = false;

    void bindBackButton(onBack).then((fn) => {
      if (cancelled) fn();
      else dispose = fn;
    });

    return () => {
      cancelled = true;
      dispose?.();
    };
  }, [isTelegram, onBack]);

  return null;
}
